import React, { useEffect, useMemo, useRef, useState } from "react";
import { Radio, Table, Typography } from "antd";
import "../DailyReports/dailyReport.css";

const { Title } = Typography;

const ANSWER_OPTIONS = [
  { label: "Yes", value: "yes" },
  { label: "No", value: "no" },
  { label: "N/A", value: "n_a" },
];

const mergeServerWithPendingAnswers = (serverItems, pendingById) => {
  const server = serverItems || [];
  if (!pendingById?.size) return server;

  return server.map((item) => {
    const questionId = String(item.question_id);
    if (!pendingById.has(questionId)) return item;
    return {
      ...item,
      answer: pendingById.get(questionId),
    };
  });
};

const SafetyQuestionsTable = ({
  inspection,
  saveAnswerAsync,
  onAnswerPersisted,
  disabled = false,
}) => {
  const [items, setItems] = useState(inspection?.items || []);
  const inspectionIdRef = useRef(inspection?._id);
  const inspectionItemsRef = useRef(inspection?.items || []);
  // Latest desired answer per question (optimistic + not yet confirmed on server).
  const pendingAnswersRef = useRef(new Map());
  // One in-flight save per question so rapid clicks cannot reorder on the wire.
  const inFlightAnswersRef = useRef(new Map());
  const saveAnswerAsyncRef = useRef(saveAnswerAsync);
  const onAnswerPersistedRef = useRef(onAnswerPersisted);
  const disabledRef = useRef(disabled);

  useEffect(() => {
    saveAnswerAsyncRef.current = saveAnswerAsync;
  }, [saveAnswerAsync]);

  useEffect(() => {
    onAnswerPersistedRef.current = onAnswerPersisted;
  }, [onAnswerPersisted]);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => {
    inspectionItemsRef.current = inspection?.items || [];
  }, [inspection]);

  useEffect(() => {
    const nextId = inspection?._id;
    const serverItems = inspection?.items || [];

    // Date-range / document change: drop pending patches and take server as-is.
    if (nextId !== inspectionIdRef.current) {
      inspectionIdRef.current = nextId;
      pendingAnswersRef.current.clear();
      inFlightAnswersRef.current.clear();
      setItems(serverItems);
      return;
    }

    setItems(
      mergeServerWithPendingAnswers(serverItems, pendingAnswersRef.current),
    );
  }, [inspection]);

  const flushAnswer = async (qid) => {
    if (disabledRef.current) return;
    const inspectionId = inspectionIdRef.current;
    if (!inspectionId) return;
    if (inFlightAnswersRef.current.get(qid)) return;
    if (!pendingAnswersRef.current.has(qid)) return;
    if (!saveAnswerAsyncRef.current) return;

    const answer = pendingAnswersRef.current.get(qid);
    inFlightAnswersRef.current.set(qid, true);

    try {
      const updated = await saveAnswerAsyncRef.current({
        inspectionId,
        inspection: {
          answer_patches: [{ question_id: qid, answer }],
        },
      });

      if (inspectionIdRef.current !== inspectionId) return;

      if (pendingAnswersRef.current.get(qid) === answer) {
        pendingAnswersRef.current.delete(qid);
        setItems((prev) =>
          prev.map((item) =>
            String(item.question_id) === qid ? { ...item, answer } : item,
          ),
        );
        if (onAnswerPersistedRef.current) {
          onAnswerPersistedRef.current(qid, answer, inspectionId, updated);
        }
      }
    } catch {
      if (inspectionIdRef.current !== inspectionId) return;

      if (pendingAnswersRef.current.get(qid) === answer) {
        pendingAnswersRef.current.delete(qid);
        const serverAnswer = (inspectionItemsRef.current || []).find(
          (item) => String(item.question_id) === qid,
        )?.answer;
        setItems((prev) =>
          prev.map((item) =>
            String(item.question_id) === qid
              ? { ...item, answer: serverAnswer ?? null }
              : item,
          ),
        );
      }
    } finally {
      inFlightAnswersRef.current.set(qid, false);
      if (pendingAnswersRef.current.has(qid)) {
        flushAnswer(qid);
      }
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    if (!inspectionIdRef.current || disabled) return;
    const qid = String(questionId);
    pendingAnswersRef.current.set(qid, answer);
    setItems((prev) =>
      prev.map((item) =>
        String(item.question_id) === qid ? { ...item, answer } : item,
      ),
    );
    flushAnswer(qid);
  };

  const columns = useMemo(
    () => [
      {
        title: "#",
        width: 56,
        render: (_value, _record, index) => index + 1,
      },
      {
        title: "Category",
        dataIndex: "category",
        width: 200,
        render: (value) => value || "—",
      },
      {
        title: "Question",
        dataIndex: "text",
      },
      {
        title: "Answer",
        width: 240,
        render: (_value, record) => (
          <Radio.Group
            disabled={disabled}
            value={record.answer ?? undefined}
            onChange={(e) =>
              handleAnswerChange(record.question_id, e.target.value)
            }
          >
            {ANSWER_OPTIONS.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        ),
      },
    ],
    [disabled],
  );

  return (
    <div>
      <div className="flex justify-between mt-2 mb-2">
        <Title level={3} style={{ margin: 0 }}>
          Questions
        </Title>
      </div>
      <Table
        rowKey={(record) => String(record.question_id)}
        bordered
        size="middle"
        columns={columns}
        dataSource={items}
        pagination={false}
        locale={{
          emptyText:
            "No questions configured. Use Add Questions to add checklist items.",
        }}
      />
    </div>
  );
};

export default SafetyQuestionsTable;
